import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";

import ToastUtil from "../../../components/ToastNotification/ToastUtil";
import { config } from "../../../config/index";
import { newFetch, POST } from "../../../helpers/fetch";
import "./styles.css";

const { API_URL } = config;

const STATUS_META = {
  assigned: { key: "assigned", label: "Assigned", icon: "fa-calendar-check" },
  present: { key: "present", label: "Present", icon: "fa-check" },
  late: { key: "late", label: "Late", icon: "fa-clock" },
  absent: { key: "absent", label: "Absent", icon: "fa-times" },
  leave: { key: "leave", label: "On leave", icon: "fa-calendar-minus" },
  ongoing: { key: "ongoing", label: "Ongoing", icon: "fa-play" },
  rest: { key: "rest", label: "Rest day", icon: "fa-minus" },
};

const getShiftStatus = (shift, isToday = false) => {
  if (shift.leave_type) return STATUS_META.leave;
  if (isToday && shift.time_in && !shift.time_out) return STATUS_META.ongoing;

  switch (Number(shift.status)) {
    case 1:
      return STATUS_META.assigned;
    case 2:
      return STATUS_META.present;
    case 3:
    case 4:
      return STATUS_META.late;
    case 5:
    case 6:
      return STATUS_META.absent;
    default:
      return STATUS_META.rest;
  }
};

const getDayStatus = (shifts, isToday) => {
  if (!shifts.length) return STATUS_META.rest;
  const statuses = shifts.map((shift) => getShiftStatus(shift, isToday));
  const priority = ["leave", "absent", "late", "ongoing", "present", "assigned"];
  return priority.map((key) => statuses.find((status) => status.key === key)).find(Boolean);
};

const formatClockTime = (value) =>
  value
    ? moment(value, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "HH:mm:ss"]).format("h:mm A")
    : "—";

const MonthSchedule = ({ client, sites, activeMonth, activeSite, t }) => {
  const token = localStorage.getItem("token");
  const componentMounted = useRef(true);
  const [schedules, setSchedules] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const monthStart = useMemo(
    () => moment(activeMonth.startDate).startOf("month"),
    [activeMonth.startDate]
  );
  const monthEnd = useMemo(
    () => moment(activeMonth.endDate).endOf("month"),
    [activeMonth.endDate]
  );

  const calendarDays = useMemo(() => {
    const firstCalendarDay = moment(monthStart).startOf("week");
    return Array.from({ length: 42 }, (_, index) =>
      moment(firstCalendarDay).add(index, "days")
    );
  }, [monthStart]);

  const shiftsByDate = useMemo(
    () =>
      schedules.reduce((dates, item) => {
        if (Number(item.site_id) !== Number(activeSite)) return dates;
        const date = moment(item.date).format("YYYY-MM-DD");
        dates[date] = [...(dates[date] || []), item];
        return dates;
      }, {}),
    [schedules, activeSite]
  );

  const fetchSchedule = async () => {
    setIsLoading(true);
    try {
      await newFetch(
        POST,
        `/associate/site/${activeSite}/assign_fixed_sched_date`,
        {
          start_date: monthStart.format("YYYY-MM-DD"),
          end_date: monthEnd.format("YYYY-MM-DD"),
        }
      );

      const response = await fetch(`${API_URL}/associate/myschedulebyweek`, {
        mode: "cors",
        method: "POST",
        withCredentials: true,
        body: JSON.stringify({
          start_date: monthStart.format("YYYY-MM-DD"),
          end_date: monthEnd.format("YYYY-MM-DD"),
          site_id: activeSite,
        }),
        headers: {
          Authorization: token,
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json; charset=utf-8",
        },
      });

      const data = await response.json();
      if (componentMounted.current) {
        data.success ? setSchedules(data.data || []) : ToastUtil.error(data.message);
      }
    } catch (error) {
      console.log("error", error);
      ToastUtil.error(error);
    } finally {
      if (componentMounted.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const syncMonthSchedule = async () => {
      const selectedSite = sites.find((item) => item.id === activeSite);
      setIsLoading(true);

      try {
        if (
          selectedSite?.enable_br_upload === false &&
          monthEnd.isSameOrAfter(moment().startOf("week"), "day")
        ) {
          let weekStart = moment(monthStart).startOf("week");
          const lastWeekStart = moment(monthEnd).startOf("week");

          // The creation endpoint is intentionally week-based. Calling it for
          // every overlapping week preserves fixed schedules and weekly limits.
          while (weekStart.isSameOrBefore(lastWeekStart, "day")) {
            await newFetch(POST, `/site/${activeSite}/schedule/create`, {
              start_date: weekStart.format("YYYY-MM-DD"),
              end_date: moment(weekStart).endOf("week").format("YYYY-MM-DD"),
            });
            weekStart = moment(weekStart).add(1, "week");
          }
        }
      } catch (error) {
        console.log("error", error);
        ToastUtil.error(error);
      } finally {
        await fetchSchedule();
      }
    };

    if (client && activeSite && sites.length) syncMonthSchedule();
    // The month and site are the existing schedule-query inputs.
  }, [activeSite, client, monthStart.format("YYYY-MM-DD"), sites]);

  const openDay = (day, shifts) => {
    if (shifts.length) setSelectedDay({ day, shifts });
  };

  return (
    <div className="schedule-calendar">
      <div className="schedule-calendar__weekdays" aria-hidden="true">
        {moment.weekdaysShort().map((day) => <span key={day}>{day}</span>)}
      </div>

      <div className="schedule-calendar__grid" aria-label={`${monthStart.format("MMMM YYYY")} schedule`}>
        {calendarDays.map((day) => {
          const dateKey = day.format("YYYY-MM-DD");
          const shifts = shiftsByDate[dateKey] || [];
          const isToday = day.isSame(moment(), "day");
          const isCurrentMonth = day.isSame(monthStart, "month");
          const status = getDayStatus(shifts, isToday);

          return (
            <button
              type="button"
              key={dateKey}
              className={`schedule-calendar__day schedule-calendar__day--${status.key}${
                isToday ? " is-today" : ""
              }${!isCurrentMonth ? " is-outside" : ""}${shifts.length ? " has-schedule" : ""}`}
              onClick={() => openDay(day, shifts)}
              disabled={!shifts.length}
              aria-label={`${day.format("dddd, D MMMM YYYY")}. ${
                shifts.length
                  ? `${shifts.length} schedule${shifts.length > 1 ? "s" : ""}, ${status.label}`
                  : "No schedule"
              }`}
            >
              <span className="schedule-calendar__date">{day.date()}</span>
              {isToday && <span className="schedule-calendar__today">Today</span>}
              {shifts.length ? (
                <span className={`schedule-calendar__status status-${status.key}`}>
                  <i className={`fas ${status.icon}`} aria-hidden="true" />
                  <span>{status.label}</span>
                </span>
              ) : (
                <span className="schedule-calendar__empty" aria-hidden="true" />
              )}
              {shifts.slice(0, 2).map((shift, index) => (
                <span className="schedule-calendar__shift" key={`${shift.schedule_id || dateKey}-${index}`}>
                  <strong>{shift.name || shift.shift || t("Schedule")}</strong>
                  <small>{formatClockTime(shift.start_time)}</small>
                </span>
              ))}
              {shifts.length > 2 && <span className="schedule-calendar__more">+{shifts.length - 2} more</span>}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div className="schedule-calendar__loading" role="status" aria-live="polite">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Updating schedule…</span>
        </div>
      )}

      <footer className="schedule-calendar__legend" aria-label="Attendance status legend">
        {[STATUS_META.present, STATUS_META.absent, STATUS_META.late, STATUS_META.assigned, STATUS_META.leave, STATUS_META.rest].map(
          (status) => (
            <span key={status.key}>
              <i className={`schedule-calendar__legend-dot status-${status.key}`} aria-hidden="true" />
              {status.label}
            </span>
          )
        )}
      </footer>

      <Modal
        ariaHideApp={false}
        isOpen={Boolean(selectedDay)}
        onRequestClose={() => setSelectedDay(null)}
        className="schedule-detail-sheet"
        overlayClassName="schedule-detail-overlay"
        contentLabel="Schedule details"
      >
        {selectedDay && (
          <div>
            <div className="schedule-detail-sheet__handle" aria-hidden="true" />
            <header className="schedule-detail-sheet__header">
              <div>
                <span>Schedule details</span>
                <h3>{selectedDay.day.format("dddd, D MMMM YYYY")}</h3>
              </div>
              <button
                type="button"
                className="schedule-detail-sheet__close"
                onClick={() => setSelectedDay(null)}
                aria-label="Close schedule details"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </header>

            <div className="schedule-detail-sheet__content">
              {selectedDay.shifts.map((shift, index) => {
                const status = getShiftStatus(shift, selectedDay.day.isSame(moment(), "day"));
                const location = shift.site || sites.find((site) => site.id === activeSite)?.name;
                return (
                  <article className="schedule-detail-card" key={`${shift.schedule_id || index}-${index}`}>
                    <div className="schedule-detail-card__title">
                      <span className={`schedule-calendar__status status-${status.key}`}>
                        <i className={`fas ${status.icon}`} aria-hidden="true" />
                        {shift.leave_type || shift.status_name || status.label}
                      </span>
                      <strong>{shift.name || shift.shift || t("Schedule")}</strong>
                    </div>

                    <dl className="schedule-detail-card__grid">
                      <div><dt>Start time</dt><dd>{formatClockTime(shift.start_time)}</dd></div>
                      <div><dt>End time</dt><dd>{formatClockTime(shift.end_time)}</dd></div>
                      <div><dt>Time in</dt><dd>{formatClockTime(shift.time_in)}</dd></div>
                      <div><dt>Time out</dt><dd>{formatClockTime(shift.time_out)}</dd></div>
                      <div className="schedule-detail-card__wide">
                        <dt>Outlet / work location</dt><dd>{location || "—"}</dd>
                      </div>
                      {Number(shift.total_overtime) > 0 && (
                        <div><dt>Overtime</dt><dd>{shift.total_overtime} hours</dd></div>
                      )}
                      {shift.is_undertime && <div><dt>Note</dt><dd>Undertime</dd></div>}
                    </dl>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MonthSchedule;
